import React, { useState, useCallback } from 'react';
import { Shield, CheckCircle } from 'lucide-react';
import { useAuth, useUI } from '../hooks';
import AustraliaIcon from '../components/icons/AustraliaIcon';
import './LoginScreen.css';

const LoginScreen: React.FC = () => {
  const { login, isLoading, error } = useAuth();
  const { showToast } = useUI();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Handle signup with Auth0 - using useCallback to avoid dependency issues
  const handleSignup = useCallback(async () => {
    try {
      setIsLoggingIn(true);
      await login(undefined, 'signup');
      // Auth0 will handle the redirect
    } catch (error) {
      showToast('error', 'Sign up failed. Please try again.');
      setIsLoggingIn(false);
    }
  }, [login, showToast]);
  
  // Handle login with Auth0 - using useCallback to avoid dependency issues
  const handleLogin = useCallback(async () => {
    try {
      setIsLoggingIn(true);
      await login(undefined, 'login');
      // Auth0 will handle the redirect
    } catch (error) {
      showToast('error', 'Login failed. Please try again.');
      setIsLoggingIn(false);
    }
  }, [login, showToast]);
  
  return (
    <div className="login-page">
      <div className="login-container">
        {/* Vertical Accent Bar - Purple to Pink Gradient */}
        <div className="accent-bar" />
        
        {/* Logo */}
        <div className="login-logo">
          <img
            src="https://www.veripay.com.au/images/VeriPay-Negative.webp"
            alt="VeriPay Logo"
            className="login-logo-image"
          />
        </div>
        
        {/* Headline */}
        <h1 className="login-headline">
          Secure your next transaction in seconds.
        </h1>
        
        {/* Subheadline */}
        <p className="login-subheadline">
          Join thousands of Australians trading safely.
        </p>
        
        {/* Trust Indicators - Horizontal Scroll on Mobile */}
        <div className="trust-section">
          <div className="trust-indicators">
            <div className="trust-indicators-container" role="list">
              <div className="trust-indicator-item" role="listitem">
                <div className="trust-icon">
                  <Shield size={28} />
                </div>
                <span className="trust-label">Bank-Level Security</span>
              </div>
              
              <div className="trust-indicator-item" role="listitem">
                <div className="trust-icon">
                  <CheckCircle size={28} />
                </div>
                <span className="trust-label">ID Verified Users</span>
              </div>
              
              <div className="trust-indicator-item" role="listitem">
                <div className="trust-icon">
                  <AustraliaIcon size={28} />
                </div>
                <span className="trust-label">Australian Owned</span>
              </div>
            </div>
          </div>
          
          {/* Testimonial */}
          <div className="testimonial-card">
            <p className="testimonial-quote">
              "VeriPay gave me the confidence to buy safely. The process is so simple!"
            </p>
            <p className="testimonial-author">- Alice W.</p>
          </div>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="login-error" role="alert">
            <p className="login-error-text">{error.message}</p>
          </div>
        )}
        
        {/* Primary CTA Button - Purple to Pink Gradient */}
        <button
          type="button"
          className="login-cta-button"
          onClick={handleSignup}
          disabled={isLoggingIn || isLoading}
          aria-label="Create free account"
          aria-busy={isLoggingIn || isLoading}
        >
          {isLoggingIn || isLoading ? (
            <span className="login-loading">
              <span className="login-spinner" />
              Creating account...
            </span>
          ) : (
            'Create Free Account'
          )}
        </button>
        
        {/* Secondary Link */}
        <div className="login-secondary-link">
          Already have an account?{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); handleLogin(); }}>
            Log in
          </a>
        </div>
        
        {/* Terms & Conditions */}
        <p className="login-terms">
          By logging in, you agree to our{' '}
          <a href="https://www.veripay.com.au/terms/" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="https://www.veripay.com.au/privacy/" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default LoginScreen;
